import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { ApiError } from '../../api/index.ts';
import type { ShopSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { actionError } from '../../components/actionError.ts';
import { ROUTES } from '../../routing/routes.ts';
import { ShopEnrollmentSection } from './ShopEnrollmentSection.tsx';
import { ShopHeader } from './ShopHeader.tsx';
import { ShopLicenceSection } from './ShopLicenceSection.tsx';
import { ShopLifecycleSection } from './ShopLifecycleSection.tsx';
import { ShopOverviewTab } from './ShopOverviewTab.tsx';
import { ShopOwnersSection } from './ShopOwnersSection.tsx';
import { ShopReportsSection } from './ShopReportsSection.tsx';

type LoadState = { kind: 'loading' } | { kind: 'ready' } | { kind: 'not-found' } | { kind: 'error'; message: string };

type TabKey = 'overview' | 'reports' | 'owners' | 'access' | 'danger';

interface TabDef {
  key: TabKey;
  label: string;
  visible: (hasPermission: (code: string) => boolean) => boolean;
}

const TAB_DEFS: TabDef[] = [
  { key: 'overview', label: 'Overview', visible: () => true },
  {
    key: 'reports',
    label: 'Reports',
    visible: (hasPermission) => hasPermission('reports:read'),
  },
  { key: 'owners', label: 'Owners', visible: () => true },
  { key: 'access', label: 'Access & licence', visible: () => true },
  {
    key: 'danger',
    label: 'Danger zone',
    visible: (hasPermission) => hasPermission('shops:write'),
  },
];

function a11yProps(key: TabKey): { id: string; 'aria-controls': string } {
  return { id: `shop-tab-${key}`, 'aria-controls': `shop-tabpanel-${key}` };
}

function NotFoundBody(): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
      <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Shop not found
        </Typography>
        <Typography sx={{ opacity: 0.8, mb: 3 }}>This shop doesn't exist, or was deleted.</Typography>
        <Button onClick={() => navigate(ROUTES.shops)}>Back to shops</Button>
      </Paper>
    </Box>
  );
}

export function ShopDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const shopId = Number(id);
  const { api, hasPermission } = useAuth();
  const canWrite = hasPermission('shops:write');
  const canManageConfig = hasPermission('config:manage');
  const [searchParams, setSearchParams] = useSearchParams();

  const [shop, setShop] = useState<ShopSummary | null>(null);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const load = useCallback(async () => {
    if (!Number.isFinite(shopId) || shopId <= 0) return;
    try {
      const res = await api.shops.get(shopId);
      setShop(res);
      setState({ kind: 'ready' });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'shop_not_found') {
        setState({ kind: 'not-found' });
      } else {
        setState({ kind: 'error', message: actionError(e) });
      }
    }
  }, [api, shopId]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleTabs = TAB_DEFS.filter((t) => t.visible(hasPermission));
  const requestedTab = searchParams.get('tab');
  const activeTab: TabKey = visibleTabs.some((t) => t.key === requestedTab) ? (requestedTab as TabKey) : 'overview';

  function selectTab(key: TabKey): void {
    setSearchParams({ tab: key });
  }

  if (!Number.isFinite(shopId) || shopId <= 0) {
    return <NotFoundBody />;
  }

  if (state.kind === 'loading') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={20} />
        <Typography>Loading shop...</Typography>
      </Box>
    );
  }

  if (state.kind === 'not-found') {
    return <NotFoundBody />;
  }

  if (state.kind === 'error' || shop === null) {
    return (
      <Alert
        role="alert"
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void load()}>
            Retry
          </Button>
        }
      >
        {state.kind === 'error' ? state.message : 'Could not load this shop.'}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <ShopHeader shop={shop} />

      <Paper sx={{ px: 1 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value: TabKey) => selectTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {visibleTabs.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} {...a11yProps(t.key)} />
          ))}
        </Tabs>
      </Paper>

      {activeTab === 'overview' && (
        <Box role="tabpanel" id="shop-tabpanel-overview" aria-labelledby="shop-tab-overview">
          <ShopOverviewTab
            shop={shop}
            canWrite={canWrite}
            canManageConfig={canManageConfig}
            canReadReports={hasPermission('reports:read')}
            canWriteReports={hasPermission('reports:write')}
            onMutated={() => void load()}
          />
        </Box>
      )}
      {activeTab === 'reports' && (
        <Box role="tabpanel" id="shop-tabpanel-reports" aria-labelledby="shop-tab-reports">
          <ShopReportsSection shop={shop} />
        </Box>
      )}
      {activeTab === 'owners' && (
        <Box role="tabpanel" id="shop-tabpanel-owners" aria-labelledby="shop-tab-owners">
          <ShopOwnersSection
            shop={shop}
            canRead={hasPermission('owners:read')}
            canManage={hasPermission('owners:write')}
            onMutated={() => void load()}
          />
        </Box>
      )}
      {activeTab === 'access' && (
        <Box
          role="tabpanel"
          id="shop-tabpanel-access"
          aria-labelledby="shop-tab-access"
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          {canWrite && <ShopEnrollmentSection shop={shop} onMutated={() => void load()} />}
          <ShopLicenceSection shop={shop} canWrite={canWrite} onMutated={() => void load()} />
        </Box>
      )}
      {activeTab === 'danger' && (
        <Box role="tabpanel" id="shop-tabpanel-danger" aria-labelledby="shop-tab-danger">
          <ShopLifecycleSection shop={shop} onMutated={() => void load()} />
        </Box>
      )}
    </Box>
  );
}

export default ShopDetailPage;
